import {
  getOrderPageDetailSchema,
  getEditorOrderPageDetailsSchema,
  createOrderPageDetailsSchema,
  orderPageUpdateSchema,
} from '@messenger/types'
import type {
  GetOrderPagesNormalized,
  GetM3OrderPagesNormalized,
  EditorOrderPageDetailsNormalized,
  GetOrderPageNormalized,
  GetM3OrderPageNormalized,
} from '@messenger/types'
import axios from '@/api/axios.config'
import { z } from 'zod'
import sanitizeCaseDates from '@/utils/sanitizers/sanitizeCaseDates'

export const getOrderPageDetails = async (
  orderPageSetId: z.infer<typeof getOrderPageDetailSchema>['orderPageSetId'],
  applicationType: z.infer<typeof getOrderPageDetailSchema>['applicationType'],
) => {
  return await axios.get<GetOrderPagesNormalized | GetM3OrderPagesNormalized>(
    `orderPageDetails?order_page_set_id=${orderPageSetId}&application_type=${applicationType}`,
  )
}

export const getEditorOrderPageDetails = async (
  orderPageSetId: z.infer<
    typeof getEditorOrderPageDetailsSchema
  >['orderPageSetId'],
  applicationType: z.infer<
    typeof getEditorOrderPageDetailsSchema
  >['applicationType'],
) => {
  const response = await axios.get<EditorOrderPageDetailsNormalized>(
    `editorOrderPageDetails?order_page_set_id=${orderPageSetId}&application_type=${applicationType}`,
  )
  if (response?.data?.case) {
    response.data.case = sanitizeCaseDates(response.data.case)
  }
  return response
}

export const createOrderPage = async (
  payload: z.infer<typeof createOrderPageDetailsSchema>,
  applicationType: z.infer<
    typeof createOrderPageDetailsSchema
  >['applicationType'],
) => {
  return await axios.post<GetOrderPageNormalized | GetM3OrderPageNormalized>(
    `createOrderPageDetails`,
    {
      ...payload,
      application_type: applicationType,
    },
  )
}

// The shared axios instance has no default timeout, and every save gate
// (Save button, Back button, page nav) latches on isSavingAtom until this
// PATCH settles — a stalled request would otherwise lock the editor until a
// hard refresh. On expiry the request rejects into save()'s per-page catch
// (toast + Datadog + Sentry via useErrorHandler).
//
// 60s, not lower: save() fans out one PATCH per page and the browser queues
// them (~6 concurrent per host on HTTP/1.1) with the axios timer running from
// hand-off — a 40-page product on a slow uplink legitimately spends tens of
// seconds in queue+transfer for the tail requests. This timeout exists for
// stall RECOVERY, not latency policing; a hang still resolves within a
// minute instead of locking the editor forever.
const UPDATE_ORDER_PAGE_TIMEOUT_MS = 60_000

export const updateOrderPage = async (
  orderPageId: string,
  payload: z.infer<typeof orderPageUpdateSchema>,
  applicationType: z.infer<typeof orderPageUpdateSchema>['applicationType'],
) => {
  return await axios.patch<{ count: number }>(
    `orderPageDetails/${orderPageId}`,
    {
      ...payload,
      application_type: applicationType,
    },
    { timeout: UPDATE_ORDER_PAGE_TIMEOUT_MS },
  )
}

export const archiveOrderPage = async (
  orderPageId: string,
  archived = false,
) => {
  return await axios.patch<{ count: number }>(
    `archiveOrderPageDetails/${orderPageId}`,
    {
      archived,
    },
  )
}
